import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FDBox,
  FDContentContainer,
  FDEmphasisSurface,
  FDPrimaryActionIconButton,
  FDSecondaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { dataAttrs } from 'Helpers/Testing';
import NewVerticalStack from 'Components/ComponentLibrary/Atoms/Layout/VerticalStack/NewVerticalStack';
import { useRefreshModalCollectionCleared } from 'Hooks/useRefreshEvent';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useBoneBackgroundColor from 'Hooks/useBoneBackgroundColor';
import { useModalContext } from 'Views/Page/Contexts/ModalContext';
import fr from 'facet-rest';
import { navigateToPage } from 'Views/Page/Content/Templates';
import { buildUseStyles, useTheme } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Collapse from '@mui/material/Collapse';
import {
  useMobileLayoutForDashboardOnlyDoNotUseElsewhere,
} from 'Views/Page/Content/Templates/ClientDashboardPageTemplate/useMobileLayoutWithPillars';
import useEventTrackingLogEvent from 'EventTracking/useEventTrackingLogEvent';
import categories from 'EventTracking/consts/categories';
import eventTypes from 'EventTracking/consts/eventTypes';
import eventSubTypes from 'EventTracking/consts/eventSubTypes';
import { useAnnouncementContext } from 'Context/AnnouncementContext';
import FullScreenDataContainer
  from 'Components/ComponentLibrary/Components/Uncategorized/DataContainer/FullScreenDataContainer';
import { metaOverrideSet } from './metaOverrideSet';

const useStyles = buildUseStyles('ClientDashboardPageTemplate', () => ({
  title: {
    '& > .FD-MuiTypography-root.FD-MuiTypography-display-md': {
      fontWeight: 600,
      fontSize: ({ isMobile }) => (isMobile ? '2.5rem' : undefined),
    },
  },
  contentContainerWrapper: {
    flex: 1,
    display: 'flex',
    '& > .FD-MuiContainer-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
}));

export const ClientDashboardPageTemplate = ({ resource }) => {
  const {
    refreshPageContentCollection,
    loadPageContentLink,
    pageContentCollection,
  } = usePageContentContext();
  const { loadModalCollectionByLink } = useModalContext();
  const theme = useTheme();
  const isMobile = useMobileLayoutForDashboardOnlyDoNotUseElsewhere();
  const [pillarsExpandedOnMobile, setPillarsExpandedOnMobile] = useState(false);
  const { logStandardizedEvent } = useEventTrackingLogEvent();

  useBoneBackgroundColor();

  // Let an announcement banner be shown on this page
  const {
    banner: {
      setCanBeShown: setBannerCanBeShown,
      initializing: bannerInitializing,
    },
  } = useAnnouncementContext();
  useEffect(() => {
    setBannerCanBeShown(true);
    return () => setBannerCanBeShown(false);
  }, [setBannerCanBeShown]);

  useRefreshModalCollectionCleared((evt) => {
    switch (evt?.detail?.modalCollection?.meta().type()) {
      case 'ScenarioAcceptanceWhyStep':
        // do NOT refresh page state on this step as the completion button already navigates to summaryPlanPage
        // if page state is refreshed, it will navigate to clientDashboard instead of summaryPlanPage
        break;
      default:
        refreshPageContentCollection();
        setPillarsExpandedOnMobile(false);
    }
  }, [refreshPageContentCollection]);

  const [shownSelectScenario, setShownSelectScenario] = useState(false);
  useEffect(() => {
    if (shownSelectScenario) {
      return;
    }
    resource?.withRelationship(fr.relationships.selectScenario.name, (rel) => {
      loadModalCollectionByLink(rel.link('related').url());
      setShownSelectScenario(true);
    }, () => null);
  }, [resource]);

  const onClickActionItem = (actionItemResource) => {
    return actionItemResource.withRelationship(
      fr.relationships.navigate.name,
      (rel) => () => {
        logStandardizedEvent(categories.foundations, eventTypes.click, eventSubTypes.actionItemCTA, {
          eventProperties: {
            actionItemType: actionItemResource.attr('type'),
            actionItemName: actionItemResource.attr('name'),
            actionItemPillar: actionItemResource.attr('pillar'),
          },
        });
        const url = rel.link('related').url();
        if (rel.meta('modal') === 'true') return loadModalCollectionByLink(url);
        return navigateToPage(url, false);
      },
      () => {
        return undefined;
      },
    );
  };

  const classes = useStyles({ isMobile });

  const pillars = (
    <MetaControl
      resource={resource}
      attrName={'pillars'}
      metaOverrideSet={metaOverrideSet}
      CustomProps={{
        queryCollection: pageContentCollection,
        onQuery: loadPageContentLink,
        onClickActionItem,
        pageResource: resource,
      }}
    />
  );

  const sectionPadding = {
    paddingX: 3,
    paddingY: 2,
  };

  if (bannerInitializing) {
    return <FullScreenDataContainer loading />;
  }

  return (
    <NewVerticalStack height="screen" overflow="auto">
      <div
        className={classes.contentContainerWrapper}
        {...dataAttrs('client-dashboard-page')}
      >
        <FDContentContainer maxWidth="xl">
          <FDBox paddingX={isMobile ? 0 : 4} height="100%">
            <MetaControl attrName="loadMessage" resource={resource} metaOverrideSet={metaOverrideSet} />
            <FDStack
              height={isMobile ? undefined : '100%'}
              width="100%"
              alignItems={isMobile ? undefined : 'center'}
              {...dataAttrs('client-dashboard-page')}
            >
              <div
                style={{
                  flex: 1,
                  display: 'flex',
                  // Swap the order of these so that the text goes first using row-reverse.
                  // If you use wrap-reverse instead then the page will scroll down initially, which looks odd
                  flexFlow: 'row-reverse wrap',
                  gap: '32px',
                  rowGap: '24px',
                  justifyContent: 'space-evenly',
                  alignItems: isMobile ? undefined : 'center',
                  overflow: 'auto',
                  height: '100%',
                }}
              >
                {
                isMobile
                  ? (
                    <FDEmphasisSurface
                      width="100%"
                    >{/* we overwrite this just below to match the design, but we still need the emphasis surface so that FD can switch the button colors to have enough contrast */}
                      <div style={{ background: theme.palette.facet.primaryBlue }}>
                        <FDStack {...sectionPadding} direction="column" alignItems="center">
                          <Collapse
                            in={pillarsExpandedOnMobile}
                            style={{ width: '100%' }}
                            timeout={theme.transitions.duration.complex}
                          >
                            <div>
                              {pillars}
                              {/*
                          Add padding between the pillars and the button below.
                          The reason spacing is not used is the Collapse is always on the DOM so you'd have weird extra spacing whenever it was collapsed
                        */}
                              <FDBox paddingY={1} />
                            </div>
                          </Collapse>
                          {
                            pillarsExpandedOnMobile
                              ? <FDPrimaryActionIconButton icon="close" onClick={() => setPillarsExpandedOnMobile(false)} />
                              : <FDSecondaryActionButton
                                {...dataAttrs('complete-next-action-button')}
                                label="Complete your next action"
                                onClick={() => setPillarsExpandedOnMobile(true)}
                              />
                          }
                        </FDStack>
                      </div>
                    </FDEmphasisSurface>
                  )
                  : (
                    <FDBox height="70vh" {...sectionPadding}>
                      {pillars}
                    </FDBox>
                  )
              }
                <FDStack
                  flex={1}
                  direction="column"
                  spacing={6}
                  width="100%"
                  alignItems={isMobile ? 'center' : 'normal'}
                  {...sectionPadding}
                >
                  <FDStack direction="column" spacing={2} maxWidth={800}>
                    <div className={classes.title}>
                      <MetaControl attrName="title" resource={resource} metaOverrideSet={metaOverrideSet} />
                    </div>
                    <MetaControl attrName="subtitle" resource={resource} metaOverrideSet={metaOverrideSet} />
                  </FDStack>
                  <MetaControl resource={resource} attrName={'progress'} metaOverrideSet={metaOverrideSet} />
                </FDStack>
              </div>
            </FDStack>
          </FDBox>
        </FDContentContainer>
      </div>
    </NewVerticalStack>
  );
};

ClientDashboardPageTemplate.defaultProps = {
};

ClientDashboardPageTemplate.propTypes = {
  resource: PropTypes.object.isRequired,
};
